import { useCallback, useMemo } from 'react';

import { useSession } from '@ais-ui/websockets';
import { UseMutationResult, useQueryClient } from '@tanstack/react-query';

import { useTrackedMutation as useMutation } from '@ais-ui/performance-tracking';
import { useCredentials } from '@ais-ui/auth';
import { useBasePath } from '@ais-ui/custom-hooks';
import {
  Configuration,
  Destination,
  type JobId,
  Port,
  RouteApi,
  Source,
} from '@ais-ui/api-spec/portserver';
import { getBasePath } from './getAISBasePath';
import { getAuthHeaderFromCredentials } from '@ais-ui/utils';
import { useApiClientWrapper } from './apiClientWrapper';

const useRoutesApi = () => {
  const credentials = useCredentials();
  const { sessionId } = useSession();
  const { protocol, hostname } = useBasePath();
  const apiClientWrapper = useApiClientWrapper();

  let basePath = getBasePath();
  let configuration: Configuration;

  if (hostname.includes('localhost')) {
    const username = credentials?.username || '';
    const password = credentials?.password || '';

    const authHeader = getAuthHeaderFromCredentials(username, password);

    configuration = new Configuration({
      basePath: `${basePath}api/portserver`,
      headers: {
        'X-Application-Name': 'marinepulse',
        Authorization: authHeader,
      },
    });
  } else {
    basePath = `${basePath}aisweb/api/portserver`;
    configuration = new Configuration({
      basePath: basePath,
      headers: {
        'Aisweb-Session': sessionId,
        'X-Application-Name': 'marinepulse',
      },
    });
  }

  const portserverApi = useMemo(
    () => new RouteApi(configuration),
    [configuration],
  );
  return apiClientWrapper(portserverApi) as RouteApi;
};

export const useCreateRoute = (): UseMutationResult<
  JobId,
  Error,
  { source: Source; destination: Destination },
  unknown
> => {
  const routesApi = useRoutesApi();
  const queryClient = useQueryClient();

  return useMutation(
    {
      mutationFn: async ({ source, destination }) => {
        return await routesApi.shipRoutePost({ json: { source, destination } });
      },
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ['getRoutes'] }),
    },
    'route:create',
  );
};

export const useRouteFetch = () => {
  const routesApi = useRoutesApi();
  const queryClient = useQueryClient();

  const fetchPort = useCallback(
    async (sourceMmsi: number, destinationPortIdOrLocode: number | string) => {
      const port: Port = {};
      const ship = { mmsi: sourceMmsi };

      if (typeof destinationPortIdOrLocode === 'number') {
        port.id = destinationPortIdOrLocode;
      } else {
        port.locode = destinationPortIdOrLocode;
      }

      try {
        const data = await routesApi.shipRouteExecutePost({
          json: {
            source: {
              ship: ship,
            },
            destination: {
              port: port,
            },
          },
        });
        queryClient.setQueryData(
          ['route', sourceMmsi, destinationPortIdOrLocode],
          data,
        );
        return data;
      } catch (error) {
        console.error('Error fetching route:', error);
        throw error;
      }
    },
    [routesApi, queryClient],
  );

  return fetchPort;
};
